import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { ITEMS, KINGDOMS, QUESTS, TITLES } from '@yuan-math/content';
import { levelForXp, type LearnerProfile, type RecommendationKind } from '@yuan-math/learning';
import {
  continueAdventure,
  currentKingdom,
  deriveTitle,
  earnedTitles,
  equippedItems,
  nextMainQuest,
  type AdventureStep,
  type Kingdom,
  type QuestDef,
  type QuestProgress,
} from '@yuan-math/world';
import { Avatar, Card, SectionHeading, Stat, type AvatarEquipment } from '@yuan-math/ui';
import { registry, useLearner } from '../../state/LearnerContext.tsx';
import { KingdomStatusChip } from '../../components/WorldMap.tsx';
import { classNameOf } from './classes.ts';

/**
 * `/adventure` — the RPG home (docs/rpg.md §34–35, §48, §52).
 *
 * §52 is the whole brief: *always give the student a clear answer to "what
 * should I do next?"*. So the page is arranged around one control, and
 * everything above it is short enough that the control is on the screen of a
 * phone without scrolling — who you are, where you are, what the quest is, and
 * then the button. Everything else is below it.
 *
 * **The button is not a decision this file makes.** `continueAdventure` in
 * `packages/world` adapts `recommendNext`, which is the platform's one answer
 * to that question and is shared with the ordinary dashboard. This page renders
 * the first step it returns and lists the rest; it does not filter, reorder or
 * hide any of them, so the adventure and the dashboard can never recommend two
 * different things to the same student on the same morning.
 *
 * **The reason is never dressed up.** A step's title is fantasy — "An old skill
 * calls for you" — but the line under the button is the recommendation engine's
 * own sentence about the mathematics, unedited (§35, §48). The fantasy is
 * allowed to name the work; only the mathematics may justify it.
 *
 * Nothing here gates anything, and nothing here reads XP to decide what to
 * offer: the level, the bar and the gold are the game (§46,
 * `docs/rpg-implementation-plan.md` §0.2), and the recommendation beside them
 * is drawn from mastery alone.
 */

/** How each kind of step announces itself: a word, an icon *and* a colour. */
const STEP_KIND: Record<RecommendationKind, { label: string; icon: string }> = {
  repair: { label: 'Foundations', icon: '🧱' },
  review: { label: 'Review', icon: '🔁' },
  continue: { label: 'In progress', icon: '⏳' },
  advance: { label: 'New ground', icon: '✨' },
};

const kingdomOf = (id: string | undefined): Kingdom | undefined =>
  id === undefined ? undefined : KINGDOMS.find((kingdom) => kingdom.id === id);

/**
 * What the adventurer is wearing, as drawing layers.
 *
 * Cosmetics only, and the whole of what equipment does anywhere in this
 * codebase (docs/rpg.md §19). Nothing on this page — least of all the
 * recommendation under the button — reads a single item.
 */
const equipmentOf = (profile: LearnerProfile): AvatarEquipment => {
  const worn = equippedItems(ITEMS, profile);
  return { held: worn.held?.art, cape: worn.cape?.art, headgear: worn.headgear?.art };
};

/**
 * The one extra sentence §35 asks for under the button.
 *
 * Counted, not rounded into a percentage: three prerequisites cannot honestly
 * produce "82%", and "2 of the 3 skills it rests on" is the same fact said in a
 * way that survives being true.
 */
function prerequisiteLine(step: AdventureStep): string | undefined {
  const { known, total } = step.prerequisites;
  if (total === 0) return 'Nothing stands underneath it — this is a place to begin.';
  if (known === 0) return undefined;
  return `You already know ${known} of the ${total} ${total === 1 ? 'skill' : 'skills'} it rests on.`;
}

/* -------------------------------------------------------------------------- */
/* The quest strip                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The kingdom's current main quest and how far through it the student is.
 *
 * Derived on every render like everything else about a quest — there is no
 * completion flag anywhere in the profile to read
 * (`docs/rpg-implementation-plan.md` §0.4).
 */
function QuestLine({ quest, progress }: { quest: QuestDef; progress: QuestProgress }): JSX.Element {
  const percent = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);
  return (
    <div className="adventure-next__quest">
      <p className="adventure-next__quest-line">
        <span className="adventure-next__label">Your quest</span>{' '}
        <Link to={`/adventure/quests/${quest.id}`} className="adventure-next__quest-link">
          <span aria-hidden="true">⚔️</span> {quest.title}
        </Link>
      </p>
      <div
        className="adventure-next__track"
        role="progressbar"
        aria-valuenow={progress.done}
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-label={`${quest.title}: ${progress.done} of ${progress.total}`}
      >
        <div className="adventure-next__fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="adventure-next__quest-count">
        {progress.done}/{progress.total} done
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The page                                                                   */
/* -------------------------------------------------------------------------- */

export function AdventureHomePage(): JSX.Element {
  const { profile } = useLearner();
  if (!profile) return <div />;

  const character = profile.rpg?.character;
  const level = levelForXp(profile.xp);
  const gold = profile.rpg?.gold ?? 0;

  // Derived per render rather than memoised, like mastery everywhere else:
  // retention decays, and a recommendation cached on a timestamp goes quietly
  // out of date on the screen.
  const now = Date.now();
  const steps = continueAdventure(KINGDOMS, QUESTS, registry, profile, now);
  const here = currentKingdom(KINGDOMS, registry, profile, now);
  const quest = here ? nextMainQuest(QUESTS, here.kingdom.id, profile, now) : undefined;
  const honorifics = earnedTitles(TITLES, profile, registry.graph, now);

  const [next, ...rest] = steps;
  const why = next ? prerequisiteLine(next) : undefined;
  /*
   * Named only when it is somewhere else. The usual case is that the next step
   * is in the kingdom the student is standing in, and "You are here: Starter
   * Village / In Starter Village" is one fact said twice. A step that leads out
   * of the kingdom — a repair sending somebody back a kingdom, a review pulling
   * them forward — is worth saying, and is the only case this line appears in.
   */
  const elsewhere = next?.kingdomId && next.kingdomId !== here?.kingdom.id ? kingdomOf(next.kingdomId) : undefined;

  return (
    <div className="page page--narrow adventure-home">
      <h1 className="page__title adventure-home__title">Your adventure</h1>

      {/* ------------------------------------------------------- who you are --- */}
      <Card className="adventure-hero">
        {/* Named for this page: the drawing is the student's own adventurer,
            and "image" with no name is what a screen reader says otherwise. The
            class is in the name because it is what the picture changed. */}
        <Avatar
          config={character?.avatar}
          classId={character?.classId}
          equipment={equipmentOf(profile)}
          size={96}
          className="adventure-hero__avatar"
          label={`${profile.displayName}, your adventurer — ${classNameOf(character?.classId)}`}
        />
        <div className="adventure-hero__body">
          <p className="adventure-hero__name">{profile.displayName}</p>
          {/*
           * The adventurer's title, above the class. Both are flavour: the
           * title is read off the game level, which is XP and habit, and says
           * nothing about mathematics (§46). The mastery figures live on the
           * dashboard and are the ones allowed to make that claim.
           */}
          <p className="adventure-hero__title">{deriveTitle(level.level)}</p>
          <p className="adventure-hero__class">{classNameOf(character?.classId)}</p>
          {honorifics.length > 0 ? (
            <ul className="title-list">
              {honorifics.map((title) => (
                <li key={title.id} className="title-chip">
                  <span aria-hidden="true">🏅</span> {title.text}
                </li>
              ))}
            </ul>
          ) : null}
          <p className="adventure-hero__level">
            Level {level.level} · {level.title}
          </p>
          {/*
           * Drawn here rather than with `MasteryBar`, which labels itself as
           * mastery to a screen reader. Game XP is not a claim about
           * mathematics (§46, CLAUDE.md §10) and must not announce itself as
           * one — the two bars measure different things and say so.
           */}
          <div
            className="xp-bar"
            role="progressbar"
            aria-valuenow={level.xpIntoLevel}
            aria-valuemin={0}
            aria-valuemax={level.xpForNextLevel}
            aria-label={`Level ${level.level} progress: ${level.xpIntoLevel} of ${level.xpForNextLevel} XP`}
          >
            <div className="xp-bar__fill" style={{ width: `${Math.round(level.fraction * 100)}%` }} />
          </div>
          <p className="adventure-hero__next">
            {level.xpForNextLevel - level.xpIntoLevel} XP to level {level.level + 1}
          </p>
        </div>
        <div className="adventure-hero__purse">
          <Stat label="Gold" value={`${gold}`} hint="🪙 earned on quests" />
          <Stat label="XP" value={`${profile.xp}`} hint="from real mathematics" />
        </div>
      </Card>

      {/* ------------------------------------------- where you are, and next --- */}
      <Card className="adventure-next">
        {here ? (
          <p className="adventure-next__here">
            <span className="adventure-next__label">You are here</span>{' '}
            <Link to={`/adventure/kingdoms/${here.kingdom.id}`} className="adventure-next__kingdom">
              <span aria-hidden="true">{here.kingdom.emoji}</span> {here.kingdom.name}
            </Link>{' '}
            <KingdomStatusChip status={here.state.status} />
          </p>
        ) : null}

        {quest ? <QuestLine quest={quest.quest} progress={quest.progress} /> : null}

        {next ? (
          <>
            <h2 className="adventure-next__title">{next.title}</h2>
            <Link to={next.href} className="btn btn--primary btn--lg btn--full adventure-next__go">
              Continue Adventure
            </Link>
            <p className="adventure-next__why">
              <strong>Why this?</strong> {next.reason}
              {why ? ` ${why}` : ''}
            </p>
            {elsewhere ? (
              <p className="adventure-next__where">
                This one is in{' '}
                <Link to={`/adventure/kingdoms/${elsewhere.id}`}>
                  <span aria-hidden="true">{elsewhere.emoji}</span> {elsewhere.name}
                </Link>
              </p>
            ) : null}
          </>
        ) : (
          <>
            <h2 className="adventure-next__title">Nothing is waiting for you</h2>
            <p className="adventure-next__why">
              Every skill the world can teach you today is behind you, and nothing is due back yet. Go exploring — the
              map is the whole of it.
            </p>
            <Link to="/adventure/map" className="btn btn--primary btn--lg btn--full adventure-next__go">
              Open the world map
            </Link>
          </>
        )}
      </Card>

      {/* ------------------------------------------------------- and after --- */}
      {rest.length > 0 ? (
        <section className="dashboard-section adventure-more">
          <SectionHeading
            title="And after that"
            description="The rest of what the mathematics suggests, in the same order. Any of them is a fine place to go instead."
          />
          <ul className="adventure-steps">
            {rest.map((step) => {
              const home = kingdomOf(step.kingdomId);
              const { label, icon } = STEP_KIND[step.kind];
              const key = `step-${step.kind}-${step.conceptId}`;
              return (
                <li key={key}>
                  {/* The name stays the step's own framing; the kind and the
                      mathematical reason are the *description*, so a screen
                      reader hears them after it rather than in front of it. */}
                  <Link
                    to={step.href}
                    aria-label={step.title}
                    aria-describedby={`${key}-kind ${key}-why`}
                    className={`card card--pressable adventure-step adventure-step--${step.kind}`}
                  >
                    <span className={`adventure-step__kind adventure-step__kind--${step.kind}`} id={`${key}-kind`}>
                      <span aria-hidden="true">{icon}</span> {label}
                    </span>
                    <span className="adventure-step__title">{step.title}</span>
                    <span className="adventure-step__reason" id={`${key}-why`}>
                      {step.reason}
                    </span>
                    {home ? (
                      <span className="adventure-step__where">
                        <span aria-hidden="true">{home.emoji}</span> {home.name}
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <div className="button-row">
        <Link to="/adventure/map" className="btn btn--secondary">
          World map
        </Link>
        <Link to="/adventure/character" className="btn btn--secondary">
          {character ? 'Edit your character' : 'Create your character'}
        </Link>
        {/* §18. Offered plainly, never as a badge with a count on it: a number
            counting the student's mistakes on their home screen is the one way
            to make a journal of them feel like a report card. */}
        <Link to="/adventure/journal" className="btn btn--secondary">
          Adventure journal
        </Link>
        <Link to="/home" className="btn btn--secondary">
          Back to learning
        </Link>
      </div>
    </div>
  );
}
